// ==============================================
// Admin Users Page
// ==============================================

import { AdminUserTokens } from '../../components/admin/AdminUserTokens';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { usePageSize } from '../../hooks/usePageSize';
import { PageSizeSelect } from '../../components/ui/PageSizeSelect';
import { readUsersListState, writeUsersListState, type UsersListState } from './usersListState';
import { describeBilledPricesReport } from './billedPricesReport';

/**
 * The Billing filter: is each user on the plan they pay for? Labels match the
 * badges in the Subscription column (PlanBillingBadge). docs/core/architecture/PLAN_OVERRIDES.md
 */
const BILLING_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: 'needs_look', label: 'Needs a look', hint: 'Mismatch, Billing unknown, Not billed on a regular plan, Payment failing, or Stripe ended' },
  { value: 'mismatch', label: 'Mismatch', hint: 'Stripe charges for a different plan, and no override was recorded' },
  { value: 'not_billed_regular', label: 'Not billed, regular plan', hint: 'On a plan that expires, with nothing in Stripe' },
  { value: 'unknown', label: 'Billing unknown', hint: 'Billed in Stripe, but which plan is not recorded yet' },
  { value: 'payment_failing', label: 'Payment failing', hint: 'Stripe could not take the last payment' },
  { value: 'stripe_ended', label: 'Stripe ended', hint: 'Live here, but Stripe ended the subscription it points to. Use “Read billed plans from Stripe” to refresh' },
  { value: 'overridden', label: 'Override', hint: 'Given a different plan on purpose, with a note' },
  { value: 'not_billed_tester', label: 'Not billed, tester plan', hint: 'On a tester or internal plan that never expires' },
  { value: 'paid', label: 'Paid as plan', hint: 'Stripe charges for exactly this plan' },
  { value: 'no_plan', label: 'No plan', hint: 'No current plan at all' },
];
import { InlineRename } from '../../components/ui/InlineRename';
import { PlanBillingBadge } from '../../components/admin/PlanBillingBadge';
import {
  MagnifyingGlassIcon,
  UserCircleIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
  TrashIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  MapPinIcon,
  XMarkIcon,
  GlobeAltIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { User } from '../../types/auth';

interface UserDevice {
  id: string;
  name: string | null;
  device_type: string;
  status: string;
  registration_code: string | null;
  last_seen_at: string | null;
  last_sync_at: string | null;
  subscription_id: string | null;
  last_known_position?: {
    lat: number;
    lon: number;
    accuracy?: number;
    timestamp: string;
  } | null;
}

/**
 * "2 minutes ago" / "12 days ago" / a date once it is old enough that the
 * exact day matters more than the elapsed time. A boat that reported this
 * morning and one that reported in April should not look alike at a glance.
 */
function formatRelativeDay(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days <= 60) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-NZ');
}

const SCROLL_KEY = 'admin-users-scroll';

/**
 * The element that scrolls the page: the nearest ancestor that is set to
 * scroll *and* is taller inside than out, otherwise the window. The admin
 * layout's content box is set to scroll but grows with the page, so today it
 * is the window that scrolls (measured in Chromium, 8 Oct 2026).
 */
function findScroller(from: HTMLElement | null): HTMLElement | null {
  for (let el = from?.parentElement ?? null; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  // The address as last written. setSearchParams(fn) would hand fn the params
  // of the render that made it (react-router 6.30), so a search timer set
  // before a filter change would write the old filters back.
  const liveParams = useRef(searchParams);
  liveParams.current = searchParams;

  // The whole list view lives in the address (usersListState.ts), so a trip
  // to a device and back — by the browser's Back or the device page's —
  // returns to the same search, filters, sort, page and open rows.
  const list = readUsersListState(searchParams);
  const { page, debouncedSearch, roleFilter, originFilter, statusFilter, packageFilter, billingFilter, sortField, sortDirection } = {
    page: list.page,
    debouncedSearch: list.search,
    // Which front door someone registered through — lets a public PassageWiki
    // account be told apart from a Vortex customer at a glance.
    originFilter: list.origin,
    roleFilter: list.role,
    statusFilter: list.status,
    packageFilter: list.packageId,
    billingFilter: list.billing,
    sortField: list.sort,
    sortDirection: list.dir,
  };
  const expandedUsers = useMemo(() => new Set(list.expanded), [list.expanded.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Changes the view by rewriting the address in place (replace, so typing
   * and paging do not fill the Back button's history). Anything that changes
   * which users match goes back to page 1 unless `keepPage` says otherwise.
   */
  const updateList = (patch: Partial<UsersListState>, { keepPage = false } = {}) => {
    const next = { ...readUsersListState(liveParams.current), ...patch };
    if (!keepPage && patch.page === undefined) next.page = 1;
    const written = writeUsersListState(next);
    liveParams.current = written;
    setSearchParams(written, { replace: true });
  };

  // The box is typed into directly; the address (and the fetch) follow 300 ms
  // later. It also follows the address when that changes underneath it.
  const [search, setSearch] = useState(list.search);
  useEffect(() => { setSearch(list.search); }, [list.search]);
  const [pageSize, setPageSize] = usePageSize('users');
  const [_selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDevices, setUserDevices] = useState<Record<string, UserDevice[]>>({});
  const [userEsriUsage, setUserEsriUsage] = useState<Record<string, any | null>>({});
  const [mapModalDevice, setMapModalDevice] = useState<UserDevice | null>(null);
  const [stripeBillingFor, setStripeBillingFor] = useState<{ id: string; name: string } | null>(null);

  const stripeBillingQuery = useQuery({
    queryKey: ['admin', 'user', stripeBillingFor?.id, 'stripe-billing'],
    queryFn: () => api.adminGetUserStripeBilling(stripeBillingFor!.id),
    enabled: !!stripeBillingFor,
  });

  // Debounce the search box → server-side search across ALL users (page 1).
  useEffect(() => {
    if (search === list.search) return;
    const t = setTimeout(() => updateList({ search }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const changePageSize = (size: number) => {
    setPageSize(size);
    updateList({});
  };

  // Fetch packages for the filter dropdown — admin endpoint so hidden/archived
  // packages are included (e.g. tester-only plans not shown publicly).
  const { data: packagesData } = useQuery({
    queryKey: ['admin', 'packages'],
    queryFn: () => api.adminGetPackages(),
  });

  // Sort so active packages appear first, then hidden, then archived.
  const sortedPackages = useMemo(() => {
    if (!packagesData) return [];
    const order: Record<string, number> = { active: 0, hidden: 1, archived: 2 };
    return [...packagesData].sort(
      (a: any, b: any) => (order[a.visibility] ?? 3) - (order[b.visibility] ?? 3),
    );
  }, [packagesData]);

  // Label appends visibility and manual-expiry tags for non-standard packages
  // so admins don't accidentally assign a hidden/archived/never-expires plan
  // thinking it's a public paid plan.
  const packageOptionLabel = (pkg: any): string => {
    const tags: string[] = [];
    if (pkg.visibility && pkg.visibility !== 'active') tags.push(pkg.visibility);
    if (pkg.manual_expiry) tags.push('no expiry');
    return tags.length ? `${pkg.name} (${tags.join(', ')})` : pkg.name;
  };

  const packageName = packagesData?.find((p: any) => p.id === packageFilter)?.name;

  const clearPackageFilter = () => updateList({ packageId: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', { page, pageSize, search: debouncedSearch, role: roleFilter, status: statusFilter, package_id: packageFilter, account_origin: originFilter, billing: billingFilter }],
    queryFn: () =>
      api.adminGetUsers({
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: debouncedSearch || undefined,
        role: roleFilter || undefined,
        account_origin: originFilter || undefined,
        status: statusFilter || undefined,
        package_id: packageFilter || undefined,
        billing: billingFilter || undefined,
      }),
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<User> }) => api.adminUpdateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setSelectedUser(null);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => api.adminDeleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const handleDeleteUser = (userId: string, userEmail: string) => {
    if (confirm(`Are you sure you want to DELETE user "${userEmail}"?\n\nThis will permanently delete:\n- The user account\n- All their devices\n- All their subscriptions\n\nThis action cannot be undone!`)) {
      deleteUserMutation.mutate(userId);
    }
  };

  // An open row needs its devices and Esri usage. Rows open from the address
  // too (a deep link such as the hardware-order "Linked" chip's ?expand=, or
  // coming back from a device), so loading follows the address, once per user.
  const requestedRows = useRef(new Set<string>());
  useEffect(() => {
    for (const userId of list.expanded) {
      if (requestedRows.current.has(userId)) continue;
      requestedRows.current.add(userId);
      void loadUserRow(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.expanded.join(',')]);

  const toggleExpandUser = (userId: string) => {
    const expanded = expandedUsers.has(userId)
      ? list.expanded.filter((id) => id !== userId)
      : [...list.expanded, userId];
    updateList({ expanded }, { keepPage: true });
  };

  // The list exactly as it is now, with the search box's latest text even if
  // the 300 ms debounce has not written it to the address yet.
  const currentListSearch = () => {
    const qs = writeUsersListState({ ...list, search }).toString();
    return qs ? `?${qs}` : '';
  };

  // Return address handed to the device detail page so its back button lands
  // on this exact view: same search, filters, sort, page and open rows.
  const deviceBackTo = () => `/admin/users${currentListSearch()}`;

  // Leaving for a device: bring the address up to date (so the browser's
  // Back returns to the same view) and remember how far down the list was.
  const rememberPlace = () => {
    const address = currentListSearch();
    if (search !== list.search) updateList({ search });
    const scroller = findScroller(pageRef.current);
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ address, top: scroller?.scrollTop ?? 0 }));
    } catch {
      // Private mode or storage off: the view still comes back, just at the top.
    }
  };

  const loadUserRow = async (userId: string) => {
    {
      // Fetch devices and Esri usage in parallel
      const promises: Promise<void>[] = [];

      if (!userDevices[userId]) {
        promises.push(
          api.adminGetUserDevices(userId).then(result => {
            if (result.success) {
              const devices: UserDevice[] = (result.data || []).map((d: any) => ({
                id: d.id,
                name: d.name,
                device_type: d.device_type,
                status: d.status,
                registration_code: d.registration_code,
                last_seen_at: d.last_seen_at,
                last_sync_at: d.last_sync_at,
                subscription_id: d.subscription_id,
                last_known_position: d.last_known_position,
              }));
              setUserDevices(prev => ({ ...prev, [userId]: devices }));
            } else {
              // Same as a failed request: stop loading rather than spin.
              setUserDevices(prev => ({ ...prev, [userId]: [] }));
            }
          }).catch(error => {
            console.error('Failed to fetch devices:', error);
            setUserDevices(prev => ({ ...prev, [userId]: [] }));
          })
        );
      }

      if (userEsriUsage[userId] === undefined) {
        promises.push(
          api.adminGetUserEsriUsage(userId).then(esriData => {
            setUserEsriUsage(prev => ({ ...prev, [userId]: esriData }));
          }).catch(error => {
            console.error('Failed to fetch Esri usage:', error);
            setUserEsriUsage(prev => ({ ...prev, [userId]: null }));
          })
        );
      }

      await Promise.all(promises);
    }
  };

  // Back on the list after a device: put the scroll position back once the
  // rows (and any open ones) have loaded, so the page is its full height.
  const pageRef = useRef<HTMLDivElement>(null);
  const scrollRestored = useRef(false);

  const handleDeleteDevice = async (deviceId: string, deviceName: string, userId: string) => {
    if (confirm(`Are you sure you want to delete device "${deviceName}"?`)) {
      try {
        const result = await api.adminDeleteDevice(deviceId);
        if (result.success) {
          // Remove device from local state
          setUserDevices(prev => ({
            ...prev,
            [userId]: prev[userId]?.filter(d => d.id !== deviceId) || [],
          }));
        }
      } catch (error) {
        console.error('Failed to delete device:', error);
      }
    }
  };

  const handleUnlinkDevice = async (deviceId: string, deviceName: string, userId: string) => {
    if (confirm(`Are you sure you want to unlink "${deviceName}"?\n\nThis will:\n- Revert the device to unregistered status\n- Clear all user data and tokens\n- Generate a new registration code\n- Remove all entitlements\n\nThe device will need to be registered again.`)) {
      try {
        const unlinkedDevice = await api.adminUnlinkDevice(deviceId);
        // Update device in local state
        setUserDevices(prev => ({
          ...prev,
          [userId]: prev[userId]?.map(d =>
            d.id === deviceId ? unlinkedDevice : d
          ) || [],
        }));
        alert('Device unlinked successfully. A new registration code has been generated.');
      } catch (error: any) {
        console.error('Failed to unlink device:', error);
        alert(`Error unlinking device: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const changeSubscriptionMutation = useMutation({
    mutationFn: ({ userId, packageId, note }: { userId: string; packageId: string | null; note?: string | null }) =>
      api.adminChangeSubscription(userId, packageId, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const handleSubscriptionChange = (userId: string, packageId: string) => {
    if (packageId === '') {
      if (confirm('Are you sure you want to remove their subscription?')) {
        changeSubscriptionMutation.mutate({ userId, packageId: null });
      }
      return;
    }
    const pkg = packagesData?.find((p: any) => p.id === packageId);
    // One dialog: OK changes the plan, Cancel does nothing. A plan other than
    // the one they pay for is recorded as an override, with this note.
    const note = window.prompt(
      `Change subscription to ${pkg?.name || packageId}?\n\nIf this is not the plan they pay for, why? (optional — shown on the Override badge)`,
      '',
    );
    if (note === null) return;
    changeSubscriptionMutation.mutate({ userId, packageId, note: note.trim() || null });
  };

  const markOverrideMutation = useMutation({
    mutationFn: ({ userId, note }: { userId: string; note: string | null }) => api.adminMarkPlanOverride(userId, note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (error: any) => alert(`Could not record the override: ${error.response?.data?.error || error.message}`),
  });

  const handleMarkOverride = (userId: string, planName: string | undefined) => {
    const note = window.prompt(`Mark ${planName || 'this plan'} as given on purpose?\n\nWhy? (optional)`, '');
    if (note === null) return;
    markOverrideMutation.mutate({ userId, note: note.trim() || null });
  };

  const refreshBilledMutation = useMutation({
    mutationFn: () => api.adminRefreshBilledPrices(),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      alert(describeBilledPricesReport(r));
    },
    onError: (error: any) => alert(`Could not read billed plans from Stripe: ${error.response?.data?.error || error.message}`),
  });

  const updateExpiryMutation = useMutation({
    mutationFn: ({ userId, currentPeriodEnd }: { userId: string; currentPeriodEnd: string }) =>
      api.adminUpdateSubscriptionExpiry(userId, currentPeriodEnd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: any) => {
      alert(`Failed to update expiry: ${error.response?.data?.error || error.message}`);
    },
  });

  const handleExpiryChange = (user: User, dateStr: string) => {
    const sub = user.subscription;
    if (!sub || !dateStr) return;
    // Access lasts through the whole chosen day (UTC end-of-day).
    const currentPeriodEnd = `${dateStr}T23:59:59.999Z`;
    const nzDate = new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-NZ');
    const deactivates = new Date(currentPeriodEnd) < new Date();
    let msg = `Set ${sub.package_name} expiry for "${user.email}" to ${nzDate}?`;
    msg += deactivates
      ? '\n\nThis date is in the past — the package will DEACTIVATE and the user falls back to the default package.'
      : '\n\nThe package stays active until this date, then deactivates automatically.';
    if (sub.stripe_subscription_id && !sub.stripe_subscription_id.startsWith('admin_')) {
      msg += '\n\nNote: this subscription is billed via Stripe — the next Stripe renewal/webhook will overwrite this date.';
    }
    if (confirm(msg)) {
      updateExpiryMutation.mutate({ userId: user.id, currentPeriodEnd });
    }
  };

  const handleRoleChange = (userId: string, role: string) => {
    if (confirm(`Are you sure you want to change this user's role to ${role}?`)) {
      updateUserMutation.mutate({ id: userId, data: { role } as any });
    }
  };

  const handleStatusChange = (userId: string, status: string) => {
    const action = status === 'suspended' ? 'suspend' : 'activate';
    if (confirm(`Are you sure you want to ${action} this user?`)) {
      updateUserMutation.mutate({ id: userId, data: { status } as any });
    }
  };

  // Sorting re-orders the loaded page only, so it keeps the page.
  const toggleSort = (field: string) => {
    if (sortField === field) {
      updateList({ dir: sortDirection === 'asc' ? 'desc' : 'asc' }, { keepPage: true });
    } else {
      updateList({ sort: field, dir: 'asc' }, { keepPage: true });
    }
  };

  const getSortValue = (user: any, field: string): string | number => {
    switch (field) {
      case 'name': return (user.name || '').toLowerCase();
      case 'subscription': return (user.subscription?.package_name || '').toLowerCase();
      case 'expiry': return user.subscription?.current_period_end || '';
      case 'role': return user.role || '';
      case 'status': return user.status || '';
      case 'joined': return user.auth_joined_at || user.created_at || '';
      // Two separate facts. "Last sign-in" is the PERSON in the portal;
      // "Device last seen" is their BOAT calling home. They are unrelated —
      // a Scout keeps heartbeating on its own device token whether or not
      // its owner ever signs in again.
      // Numeric, not string: sorting "10" as text puts it below "9".
      case 'deviceCount': return user.device_count ?? 0;
      case 'lastSignIn': return user.last_sign_in_at || '';
      case 'deviceLastSeen': return user.device_last_seen_at || '';
      default: return '';
    }
  };

  // Search is now performed server-side across the whole dataset; the loaded
  // page is only re-ordered client-side per the active sort column.
  const filtered = data?.users;

  const filteredUsers = filtered && sortField
    ? [...filtered].sort((a, b) => {
        const aVal = getSortValue(a, sortField);
        const bVal = getSortValue(b, sortField);
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        return sortDirection === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const lastPage = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  // A page past the end (users deleted since, or a hand-edited address).
  useEffect(() => {
    if (data && page > lastPage) updateList({ page: lastPage }, { keepPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page, lastPage]);

  useEffect(() => {
    if (scrollRestored.current || !data) return;
    let saved: { address?: string; top?: number } | null = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) ?? 'null');
    } catch {
      saved = null;
    }
    // Compared in written-out form, so a link that listed the same view's
    // parameters in another order still counts as the same place.
    const here = writeUsersListState(readUsersListState(new URLSearchParams(location.search))).toString();
    const there = writeUsersListState(readUsersListState(new URLSearchParams(saved?.address ?? ''))).toString();
    // Only on the way back: the browser's Back/Forward (POP) or the device
    // page's own Back, which marks its return. Reaching the list any other way
    // (the sidebar, a link) starts at the top even if the address matches.
    const returning = navigationType === 'POP' || (location.state as { restorePlace?: boolean } | null)?.restorePlace === true;
    if (!saved || !returning || here !== there || typeof saved.top !== 'number') {
      scrollRestored.current = true;
      return;
    }
    // Wait for the open rows on this page to finish loading; their height counts.
    const onPage = new Set(data.users.map((u) => u.id));
    const pending = list.expanded.some((id) => onPage.has(id) && (userDevices[id] === undefined || userEsriUsage[id] === undefined));
    if (pending) return;
    scrollRestored.current = true;
    try { sessionStorage.removeItem(SCROLL_KEY); } catch { /* nothing to clean up */ }
    const scroller = findScroller(pageRef.current);
    if (scroller) scroller.scrollTop = saved.top;
  });

  return (
    <div ref={pageRef} className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-slate-400">Manage user accounts and permissions</p>
        </div>
        <button
          type="button"
          onClick={() => refreshBilledMutation.mutate()}
          disabled={refreshBilledMutation.isPending}
          className="px-3 py-2 text-sm text-slate-200 bg-slate-800 border border-slate-600 rounded-lg hover:bg-slate-700 disabled:opacity-50"
          title="Read which plan Stripe charges each customer for and whether Stripe still charges, to mark plans that differ. Also lists Stripe subscriptions nothing here points to. Changes no plan."
        >
          {refreshBilledMutation.isPending ? 'Reading Stripe…' : 'Read billed plans from Stripe'}
        </button>
      </div>

      {/* Package filter banner */}
      {packageFilter && packageName && (
        <div className="flex items-center gap-3 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
          <span className="text-sm text-cyan-400">
            Showing subscribers to <span className="font-semibold">{packageName}</span>
          </span>
          <button
            onClick={clearPackageFilter}
            className="p-1 text-cyan-400 hover:text-white rounded hover:bg-slate-700"
            title="Clear filter"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
          />
        </div>

        <select
          value={originFilter}
          onChange={(e) => updateList({ origin: e.target.value })}
          title="Which front door the account registered through"
          className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Origins</option>
          <option value="vortex">Vortex customers</option>
          <option value="passagewiki">PassageWiki (public)</option>
        </select>

        <select
          value={roleFilter}
          onChange={(e) => updateList({ role: e.target.value })}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
          <option value="tester">Tester</option>
          <option value="tester_plus">Tester+</option>
        </select>

        <select
          value={packageFilter}
          onChange={(e) => updateList({ packageId: e.target.value })}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Packages</option>
          {sortedPackages.map((pkg: any) => (
            <option key={pkg.id} value={pkg.id}>{packageOptionLabel(pkg)}</option>
          ))}
        </select>

        <select
          value={billingFilter}
          onChange={(e) => updateList({ billing: e.target.value })}
          aria-label="Billing"
          title={BILLING_OPTIONS.find((o) => o.value === billingFilter)?.hint ?? 'Is each user on the plan they pay for?'}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Billing</option>
          {BILLING_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} title={o.hint}>{o.label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => updateList({ status: e.target.value })}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-slate-400 border-b border-slate-700 bg-slate-800/50">
                  {[
                    { key: 'name', label: 'User' },
                    { key: 'subscription', label: 'Subscription' },
                    { key: 'expiry', label: 'Expiry' },
                    { key: 'status', label: 'Status' },
                    { key: 'joined', label: 'Joined' },
                    { key: 'deviceCount', label: 'Devices' },
                    { key: 'lastSignIn', label: 'Last sign-in' },
                    { key: 'deviceLastSeen', label: 'Device last seen' },
                  ].map(col => (
                    <th
                      key={col.key}
                      className="px-6 py-3 font-medium cursor-pointer select-none hover:text-white transition-colors"
                      onClick={() => toggleSort(col.key)}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {sortField === col.key ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon className="w-3.5 h-3.5 text-cyan-400" />
                          ) : (
                            <ChevronDownIcon className="w-3.5 h-3.5 text-cyan-400" />
                          )
                        ) : (
                          <ChevronUpDownIcon className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" />
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-3 font-medium">Role</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredUsers?.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr className="hover:bg-slate-800/30">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {/* Expand/Collapse button */}
                          <button
                            onClick={() => toggleExpandUser(user.id)}
                            className="p-1 text-slate-400 hover:text-white rounded hover:bg-slate-700"
                            title={expandedUsers.has(user.id) ? 'Collapse' : 'View devices'}
                          >
                            {expandedUsers.has(user.id) ? (
                              <ChevronUpIcon className="w-4 h-4" />
                            ) : (
                              <ChevronDownIcon className="w-4 h-4" />
                            )}
                          </button>
                          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <UserCircleIcon className="w-6 h-6 text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="flex items-center gap-1.5 font-medium text-white">
                              <InlineRename
                                value={user.name || ''}
                                display={(name) => <span>{name || 'Unnamed'}</span>}
                                onSave={async (name) => {
                                  await api.adminUpdateUser(user.id, { name });
                                  await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
                                }}
                                label="Rename user"
                                hint="Used to greet them in emails."
                              />
                              {user.account_origin === 'passagewiki' && (
                                <span
                                  title="Registered through PassageWiki — a public account, not a Vortex customer"
                                  className="rounded border border-indigo-400/50 bg-indigo-400/10 px-1 py-px text-[10px] font-semibold tracking-wide text-indigo-300"
                                >PW</span>
                              )}
                            </p>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                          {/* Map button - show if user has devices with GPS data */}
                          {userDevices[user.id]?.some(d => d.last_known_position) && (
                            <button
                              onClick={() => {
                                const deviceWithGps = userDevices[user.id]?.find(d => d.last_known_position);
                                if (deviceWithGps) setMapModalDevice(deviceWithGps);
                              }}
                              className="p-1.5 text-slate-400 hover:text-blue-400 rounded hover:bg-slate-700"
                              title="View Device Location"
                            >
                              <MapPinIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <select
                          value={(user as any).subscription?.package_id || ''}
                          onChange={(e) => handleSubscriptionChange(user.id, e.target.value)}
                          className="min-w-0 flex-1 max-w-[11rem] px-2 py-1 text-xs bg-slate-700 border border-slate-600 rounded text-white focus:outline-none"
                          disabled={changeSubscriptionMutation.isPending}
                        >
                          <option value="">No subscription</option>
                          {sortedPackages.map((pkg: any) => (
                            <option key={pkg.id} value={pkg.id}>{packageOptionLabel(pkg)}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() =>
                            setStripeBillingFor({
                              id: user.id,
                              name: user.name || user.email || 'User',
                            })
                          }
                          disabled={!(user as any).stripe_customer_id}
                          className="shrink-0 p-1.5 text-slate-400 hover:text-cyan-400 rounded hover:bg-slate-700 disabled:opacity-35 disabled:pointer-events-none disabled:hover:text-slate-400"
                          title={
                            (user as any).stripe_customer_id
                              ? 'View Stripe billing (payments & payment method)'
                              : 'No Stripe customer linked'
                          }
                        >
                          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        </button>
                      </div>
                      {(user as any).subscription && (
                        <PlanBillingBadge
                          sub={(user as any).subscription}
                          busy={markOverrideMutation.isPending}
                          onMarkOverride={() => handleMarkOverride(user.id, (user as any).subscription?.package_name)}
                        />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const sub = user.subscription;
                        if (!sub) return <span className="text-sm text-slate-500">—</span>;
                        // Manual-expiry packages carry a year-9999 sentinel and bypass
                        // the period-end entitlement check — nothing to edit.
                        const noExpiry = sub.package_manual_expiry ||
                          (sub.current_period_end && new Date(sub.current_period_end).getUTCFullYear() >= 9000);
                        if (noExpiry) return <span className="text-sm text-slate-500">No expiry</span>;
                        const expired = sub.current_period_end ? new Date(sub.current_period_end) < new Date() : false;
                        return (
                          <input
                            type="date"
                            value={sub.current_period_end ? sub.current_period_end.slice(0, 10) : ''}
                            onChange={(e) => handleExpiryChange(user, e.target.value)}
                            disabled={updateExpiryMutation.isPending}
                            style={{ colorScheme: 'dark' }}
                            className={`px-2 py-1 text-xs bg-slate-700 border rounded focus:outline-none focus:border-cyan-500 ${
                              expired ? 'border-red-500/60 text-red-400' : 'border-slate-600 text-white'
                            }`}
                            title={expired
                              ? 'Expired — package is deactivated (user falls back to the default package)'
                              : 'Package active until this date'}
                          />
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          user.status === 'active'
                            ? 'bg-green-500/20 text-green-400'
                            : user.status === 'suspended'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-slate-500/20 text-slate-400'
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {new Date(user.auth_joined_at || user.created_at).toLocaleDateString('en-NZ')}
                    </td>
                    <td className="px-6 py-4 text-sm tabular-nums">
                      {user.device_count && user.device_count > 0 ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); void toggleExpandUser(user.id); }}
                          className="text-slate-200 hover:text-cyan-400 transition-colors"
                          title={`Show this user's ${user.device_count} device${user.device_count === 1 ? '' : 's'}`}
                        >
                          {user.device_count}
                        </button>
                      ) : (
                        <span className="text-slate-600">0</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {user.last_sign_in_at
                        ? new Date(user.last_sign_in_at).toLocaleDateString('en-NZ')
                        : <span className="text-slate-500">Never</span>}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {user.device_last_seen_at ? (
                        <span
                          title={new Date(user.device_last_seen_at).toLocaleString('en-NZ')}
                          className={
                            Date.now() - new Date(user.device_last_seen_at).getTime() < 7 * 86400000
                              ? 'text-green-400'
                              : Date.now() - new Date(user.device_last_seen_at).getTime() < 30 * 86400000
                                ? 'text-amber-400'
                                : 'text-red-400'
                          }
                        >
                          {formatRelativeDay(user.device_last_seen_at)}
                        </span>
                      ) : (
                        <span className="text-slate-500">No active Scout</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {/* Role change */}
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          className="px-2 py-1 text-xs bg-slate-700 border border-slate-600 rounded text-white focus:outline-none"
                          disabled={updateUserMutation.isPending}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                          <option value="tester">Tester</option>
                          <option value="tester_plus">Tester+</option>
                        </select>

                        {/* Status toggle */}
                        {user.status === 'active' ? (
                          <button
                            onClick={() => handleStatusChange(user.id, 'suspended')}
                            className="p-1.5 text-slate-400 hover:text-red-400 rounded hover:bg-slate-700"
                            title="Suspend User"
                            disabled={updateUserMutation.isPending}
                          >
                            <NoSymbolIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(user.id, 'active')}
                            className="p-1.5 text-slate-400 hover:text-green-400 rounded hover:bg-slate-700"
                            title="Activate User"
                            disabled={updateUserMutation.isPending}
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                        )}

                        {/* Delete button - only show for non-super_admin users */}
                        {user.role !== 'super_admin' && (
                          <button
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-slate-700"
                            title="Delete User"
                            disabled={deleteUserMutation.isPending}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded devices row */}
                  {expandedUsers.has(user.id) && (
                    <tr className="bg-slate-900/50">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="ml-8 space-y-4">
                          <AdminUserTokens userId={user.id} />
                          {/* Esri Tile Consumption Block */}
                          {userEsriUsage[user.id] !== undefined && (() => {
                            const esri = userEsriUsage[user.id];
                            if (!esri) return null;

                            const usagePct = Math.min(esri.usage_percentage, 100);
                            const barColor = usagePct >= 90 ? 'bg-red-500'
                              : usagePct >= 70 ? 'bg-amber-500'
                              : usagePct >= 40 ? 'bg-cyan-500'
                              : 'bg-green-500';
                            const changeColor = esri.tiles_change_percent > 0 ? 'text-amber-400'
                              : esri.tiles_change_percent < 0 ? 'text-green-400'
                              : 'text-slate-500';
                            const fmt = (n: number) => n.toLocaleString();

                            return (
                              <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
                                {/* Header */}
                                <div className="px-4 py-3 flex items-center justify-between border-b border-slate-700/50">
                                  <div className="flex items-center gap-2">
                                    <GlobeAltIcon className="w-4 h-4 text-cyan-400" />
                                    <span className="text-sm font-medium text-white">Esri Tile Usage</span>
                                    {esri.package_name && (
                                      <span className="px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">
                                        {esri.package_name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-sm">
                                    <span className="text-white font-medium font-mono">{fmt(esri.tiles_this_month)}</span>
                                    {esri.tiles_this_month > 0 && (
                                      <span className={`text-xs ${changeColor}`}>
                                        {esri.tiles_change_percent > 0 ? '+' : ''}{esri.tiles_change_percent}%
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Progress bar */}
                                <div className="px-4 py-3">
                                  <div className="flex items-center gap-3 mb-1">
                                    <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${barColor}`}
                                        style={{ width: `${Math.max(usagePct, esri.tiles_this_month > 0 ? 1 : 0)}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-slate-400 w-14 text-right">
                                      {usagePct < 0.1 && esri.tiles_this_month > 0 ? '<0.1' : usagePct.toFixed(1)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between text-xs text-slate-500">
                                    <span>{fmt(esri.tiles_this_month)} / {fmt(esri.free_tiles_per_month)} free tiles</span>
                                    {esri.estimated_cost_usd > 0 ? (
                                      <span className="text-amber-400">Est. cost: ${esri.estimated_cost_usd.toFixed(2)}</span>
                                    ) : (
                                      <span className="text-green-400">Within free tier</span>
                                    )}
                                  </div>
                                </div>

                                {/* Per-device breakdown */}
                                {esri.devices.length > 0 && (
                                  <div className="border-t border-slate-700/50 px-4 py-2">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-slate-500">
                                          <th className="text-left pb-1 font-medium">Device</th>
                                          <th className="text-right pb-1 font-medium">This Month</th>
                                          <th className="text-right pb-1 font-medium">Last Month</th>
                                          <th className="text-right pb-1 font-medium">Change</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-700/30">
                                        {esri.devices.map((d: any) => {
                                          const dc = d.tiles_last_month > 0
                                            ? Math.round(((d.tiles_this_month - d.tiles_last_month) / d.tiles_last_month) * 100)
                                            : d.tiles_this_month > 0 ? 100 : 0;
                                          return (
                                            <tr key={d.device_id}>
                                              <td className="py-1.5">
                                                <Link
                                                  to={`/admin/devices/${d.device_id}`}
                                                  state={{ from: deviceBackTo() }}
                                                  onClick={rememberPlace}
                                                  className="text-white hover:text-cyan-400 hover:underline"
                                                  title="Open this device's detail page"
                                                >
                                                  {d.device_name || d.serial_number || d.device_id.slice(0, 8)}
                                                </Link>
                                              </td>
                                              <td className="py-1.5 text-right text-slate-300 font-mono">{fmt(d.tiles_this_month)}</td>
                                              <td className="py-1.5 text-right text-slate-500 font-mono">{fmt(d.tiles_last_month)}</td>
                                              <td className={`py-1.5 text-right ${dc > 0 ? 'text-amber-400' : dc < 0 ? 'text-green-400' : 'text-slate-500'}`}>
                                                {dc > 0 ? '+' : ''}{dc}%
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          <h4 className="text-sm font-medium text-white mb-3">
                            Devices ({userDevices[user.id]?.length || 0})
                          </h4>
                          {!userDevices[user.id] ? (
                            <div className="flex items-center gap-2 text-slate-400">
                              <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                              Loading devices...
                            </div>
                          ) : userDevices[user.id].length === 0 ? (
                            <p className="text-slate-500 text-sm">No devices registered</p>
                          ) : (
                            <div className="space-y-2">
                              {userDevices[user.id].map((device) => (
                                <div
                                  key={device.id}
                                  className="bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {device.device_type === 'mobile' ? (
                                        <DevicePhoneMobileIcon className="w-5 h-5 text-slate-400" />
                                      ) : (
                                        <ComputerDesktopIcon className="w-5 h-5 text-slate-400" />
                                      )}
                                      <div>
                                        <Link
                                          to={`/admin/devices/${device.id}`}
                                          state={{ from: deviceBackTo() }}
                                          onClick={rememberPlace}
                                          className="text-white font-medium hover:text-cyan-400 hover:underline"
                                          title="Open this device's detail page"
                                        >
                                          {device.name || 'Unnamed Device'}
                                        </Link>
                                        <div className="flex items-center gap-2 text-xs text-slate-400">
                                          <span className="capitalize">{device.device_type}</span>
                                          <span>•</span>
                                          <span
                                            className={`px-1.5 py-0.5 rounded ${
                                              device.status === 'active'
                                                ? 'bg-green-500/20 text-green-400'
                                                : device.status === 'pending'
                                                ? 'bg-yellow-500/20 text-yellow-400'
                                                : 'bg-slate-500/20 text-slate-400'
                                            }`}
                                          >
                                            {device.status}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {device.status !== 'unregistered' && (
                                        <button
                                          onClick={() => handleUnlinkDevice(device.id, device.name || 'Unnamed Device', user.id)}
                                          className="px-2 py-1 text-xs bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50 rounded transition-colors"
                                          title="Unlink device and generate new registration code"
                                        >
                                          Unlink
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleDeleteDevice(device.id, device.name || 'Unnamed Device', user.id)}
                                        className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-slate-700"
                                        title="Delete Device"
                                      >
                                        <TrashIcon className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Device details grid */}
                                  <div className="mt-3 pt-3 border-t border-slate-700 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                    {/* Connect Code */}
                                    <div>
                                      <span className="text-slate-500 block">Connect Code</span>
                                      {device.registration_code ? (
                                        <span className="font-mono text-cyan-400 bg-slate-700 px-2 py-1 rounded inline-block mt-1">
                                          {device.registration_code}
                                        </span>
                                      ) : (
                                        <span className="text-slate-600 mt-1 block">-</span>
                                      )}
                                    </div>

                                    {/* Last LA Sync */}
                                    <div>
                                      <span className="text-slate-500 block">Last LA Sync</span>
                                      {device.last_sync_at ? (
                                        <span className="text-white mt-1 block">
                                          {new Date(device.last_sync_at).toLocaleString('en-NZ')}
                                        </span>
                                      ) : (
                                        <span className="text-slate-600 mt-1 block">Never</span>
                                      )}
                                    </div>

                                    {/* Last Seen */}
                                    <div>
                                      <span className="text-slate-500 block">Last Seen</span>
                                      {device.last_seen_at ? (
                                        <span className="text-slate-400 mt-1 block">
                                          {new Date(device.last_seen_at).toLocaleString('en-NZ')}
                                        </span>
                                      ) : (
                                        <span className="text-slate-600 mt-1 block">Never</span>
                                      )}
                                    </div>

                                    {/* Subscription Link */}
                                    <div>
                                      <span className="text-slate-500 block">Subscription</span>
                                      <span className={`mt-1 block ${device.subscription_id ? 'text-green-400' : 'text-yellow-400'}`}>
                                        {device.subscription_id ? 'Linked' : 'Not linked'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}

                {filteredUsers?.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-slate-400">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination + page size */}
            {data && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-slate-700">
                <div className="flex items-center gap-4">
                  <PageSizeSelect value={pageSize} onChange={changePageSize} />
                  <p className="text-sm text-slate-400">
                    {data.total === 0
                      ? 'No users'
                      : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, data.total)} of ${data.total} users`}
                  </p>
                </div>
                {data.total > pageSize && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateList({ page: page - 1 }, { keepPage: true })}
                      disabled={page === 1}
                      aria-label="Previous page"
                      title="Previous page"
                      className="p-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-white">
                      Page {page} of {lastPage}
                    </span>
                    <button
                      onClick={() => updateList({ page: page + 1 }, { keepPage: true })}
                      disabled={page >= lastPage}
                      aria-label="Next page"
                      title="Next page"
                      className="p-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* GPS Location Modal */}
      {/* Stripe billing (admin) */}
      {stripeBillingFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden shadow-xl border border-slate-700">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">Stripe billing</h2>
                <p className="text-sm text-slate-400 mt-0.5">{stripeBillingFor.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setStripeBillingFor(null)}
                className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700"
                aria-label="Close"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-5rem)] text-sm">
              {stripeBillingQuery.isLoading && (
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                  Loading from Stripe…
                </div>
              )}
              {stripeBillingQuery.isError && (
                <p className="text-red-400">
                  {(stripeBillingQuery.error as any)?.response?.data?.error ??
                    (stripeBillingQuery.error as Error)?.message ??
                    'Failed to load Stripe data.'}
                </p>
              )}
              {stripeBillingQuery.data?.success && stripeBillingQuery.data.data && (() => {
                const d = stripeBillingQuery.data.data;
                const totalsByCurrency = d.totals_by_currency ?? [];
                const paymentsList = d.payments ?? [];
                const subscriptionsList = d.subscriptions ?? [];

                const fmt = (cents: number, cur: string) => {
                  try {
                    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(
                      cents / 100,
                    );
                  } catch {
                    return `${(cents / 100).toFixed(2)} ${cur}`;
                  }
                };

                const dashCustomer = d.stripe_dashboard_customer_url;

                return (
                  <div className="space-y-5">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h3 className="text-base font-semibold text-white">
                          {d.customer_name || stripeBillingFor.name}
                        </h3>
                        {d.customer_email && (
                          <p className="text-sm text-slate-400 mt-0.5">{d.customer_email}</p>
                        )}
                      </div>
                      {dashCustomer && (
                        <a
                          href={dashCustomer}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                        >
                          Open in Stripe
                          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>

                    <div>
                      <span className="text-slate-500 block text-xs uppercase tracking-wide mb-1">
                        Stripe customer ID
                      </span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-cyan-300 font-mono text-xs break-all">{d.stripe_customer_id}</code>
                        <button
                          type="button"
                          onClick={() => void navigator.clipboard.writeText(d.stripe_customer_id)}
                          className="p-1 text-slate-400 hover:text-white rounded hover:bg-slate-700"
                          title="Copy"
                        >
                          <ClipboardDocumentIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {subscriptionsList.length > 0 && (
                      <div>
                        <span className="text-slate-500 block text-xs uppercase tracking-wide mb-2">
                          Subscriptions
                        </span>
                        <ul className="space-y-2 border border-slate-700 rounded-lg divide-y divide-slate-700/80">
                          {subscriptionsList.map((sub) => (
                            <li key={sub.id} className="px-3 py-2 text-sm">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-white font-medium">{sub.product_name}</span>
                                <span className="px-1.5 py-0.5 rounded text-xs bg-slate-600 text-slate-200 capitalize">
                                  {sub.status.replace(/_/g, ' ')}
                                </span>
                                {sub.cancel_summary && (
                                  <span className="text-xs text-slate-400">{sub.cancel_summary}</span>
                                )}
                              </div>
                              <p className="text-slate-400 text-xs mt-1">{sub.frequency_label}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div>
                      <span className="text-slate-500 block text-xs uppercase tracking-wide mb-1">
                        Total paid (successful charges)
                      </span>
                      {totalsByCurrency.length === 0 ? (
                        <p className="text-slate-400">No successful charges in the last 100 records.</p>
                      ) : (
                        <ul className="space-y-1 text-white">
                          {totalsByCurrency.map((t) => (
                            <li key={t.currency}>
                              {fmt(t.total_paid_cents, t.currency)}{' '}
                              <span className="text-slate-500">
                                ({t.charge_count} charge{t.charge_count === 1 ? '' : 's'})
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {d.truncated && (
                        <p className="text-amber-400/90 text-xs mt-2">
                          Totals may be incomplete — this customer has more than 100 charges in Stripe.
                        </p>
                      )}
                    </div>

                    {paymentsList.length > 0 && (
                      <div>
                        <span className="text-slate-500 block text-xs uppercase tracking-wide mb-2">
                          Payments (last 3)
                        </span>
                        <ul className="space-y-2 border border-slate-700 rounded-lg divide-y divide-slate-700/80">
                          {paymentsList.map((p, i) => (
                            <li
                              key={`${p.created}-${i}`}
                              className="px-3 py-2 flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-start sm:gap-4"
                            >
                              <div className="min-w-0 flex-1">
                                <span className="text-slate-300 block truncate">
                                  {p.description || 'Payment'}
                                </span>
                                <p className="text-xs text-slate-500 mt-1">
                                  {p.payment_method_label || '—'}
                                </p>
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                  {p.hosted_invoice_url && (
                                    <a
                                      href={p.hosted_invoice_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-cyan-400 hover:text-cyan-300"
                                    >
                                      View invoice
                                    </a>
                                  )}
                                  {p.stripe_dashboard_invoice_url && (
                                    <a
                                      href={p.stripe_dashboard_invoice_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-slate-400 hover:text-slate-300"
                                    >
                                      In Stripe
                                    </a>
                                  )}
                                </div>
                              </div>
                              <span className="text-white shrink-0 font-medium sm:pt-0.5">
                                {fmt(p.amount_cents, p.currency)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {mapModalDevice && mapModalDevice.last_known_position && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  Device Location
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  {mapModalDevice.name || 'Unnamed Device'}
                </p>
              </div>
              <button
                onClick={() => setMapModalDevice(null)}
                className="text-slate-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Location Info */}
              <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 block">Latitude</span>
                  <span className="text-white font-mono">
                    {mapModalDevice.last_known_position.lat.toFixed(6)}°
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Longitude</span>
                  <span className="text-white font-mono">
                    {mapModalDevice.last_known_position.lon.toFixed(6)}°
                  </span>
                </div>
                {mapModalDevice.last_known_position.accuracy && (
                  <div>
                    <span className="text-slate-500 block">Accuracy</span>
                    <span className="text-white">
                      ±{mapModalDevice.last_known_position.accuracy.toFixed(1)}m
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-slate-500 block">Reported</span>
                  <span className="text-white">
                    {new Date(mapModalDevice.last_known_position.timestamp).toLocaleString('en-NZ')}
                  </span>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="relative w-full h-[500px] bg-slate-900 rounded-lg overflow-hidden">
                <iframe
                  className="w-full h-full"
                  frameBorder="0"
                  style={{ border: 0 }}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${mapModalDevice.last_known_position.lat},${mapModalDevice.last_known_position.lon}&t=k&z=15&output=embed`}
                  allowFullScreen
                />
              </div>

              {/* Google Maps Link */}
              <div className="mt-4 flex justify-end">
                <a
                  href={`https://www.google.com/maps?q=${mapModalDevice.last_known_position.lat},${mapModalDevice.last_known_position.lon}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open in Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsersPage;
